import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from "@tanstack/react-query";
import type { Channel, Notification } from "@agentsaas/protocol";
import { useServices } from "../app/services";
import { isEvent, useChannel } from "./realtime";
import type { NotifyCategory, NotifyPref } from "../notifications/model";

/**
 * P3 通知数据：列表（游标分页）、已读、偏好。
 * user:<uid> 通道的 notification.new 只带 id/code/params（没有 link / createdAt），收到后重新拉第一页。
 * 偏好没有 GET 接口：PUT /me/notification-prefs 是合并语义，传 {} 即返回当前完整偏好。
 */

export const nk = {
  list: ["notifications"] as const,
  prefs: ["notificationPrefs"] as const,
};

type Page = { items: Notification[]; nextCursor: string | null };
const PAGE = 30;

export function useNotifications(enabled: boolean) {
  const { api } = useServices();
  const q = useInfiniteQuery({
    queryKey: nk.list,
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.call("notificationList", { query: { limit: PAGE, ...(pageParam ? { cursor: pageParam } : {}) } }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 60_000,
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  return {
    items,
    unread: items.filter((n) => !n.read).length,
    loading: q.isPending && enabled,
    hasMore: !!q.hasNextPage,
    loadMore: () => void q.fetchNextPage(),
    loadingMore: q.isFetchingNextPage,
  };
}

/** 本地把一批通知标成已读（乐观更新） */
function markLocal(data: InfiniteData<Page> | undefined, ids: Set<string> | "all"): InfiniteData<Page> | undefined {
  if (!data) return data;
  return { ...data, pages: data.pages.map((p) => ({ ...p, items: p.items.map((n) => (ids === "all" || ids.has(n.id) ? { ...n, read: true } : n)) })) };
}

export function useMarkRead() {
  const { api } = useServices();
  const qc = useQueryClient();
  return async (ids: string[] | "all") => {
    const prev = qc.getQueryData<InfiniteData<Page>>(nk.list);
    qc.setQueryData<InfiniteData<Page>>(nk.list, (d) => markLocal(d, ids === "all" ? "all" : new Set(ids)));
    try {
      await api.call("notificationRead", { body: ids === "all" ? { all: true } : { ids } });
    } catch (e) {
      qc.setQueryData(nk.list, prev);
      throw e;
    }
  };
}

/** 订阅 user:<uid>：新通知到达时刷新列表并回调（用于前台 toast） */
export function useUserNotifications(uid: string | undefined, onNew: (n: { id: string; code: string; params: Record<string, unknown> }) => void) {
  const qc = useQueryClient();
  useChannel(uid ? (`user:${uid}` as Channel) : null, {
    onEvent(e) {
      if (!isEvent(e, "notification.new")) return;
      void qc.invalidateQueries({ queryKey: nk.list });
      onNew(e.data);
    },
    onGap() {
      void qc.invalidateQueries({ queryKey: nk.list });
    },
  });
}

export type Prefs = Record<NotifyCategory, NotifyPref>;

export function useNotificationPrefs() {
  const { api } = useServices();
  return useQuery({ queryKey: nk.prefs, queryFn: async () => (await api.call("notificationPrefs", { body: {} })) as Prefs, staleTime: 5 * 60_000 });
}

export function useSetPref() {
  const { api } = useServices();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ category, pref }: { category: NotifyCategory; pref: NotifyPref }) => (await api.call("notificationPrefs", { body: { [category]: pref } })) as Prefs,
    onMutate: ({ category, pref }) => {
      const prev = qc.getQueryData<Prefs>(nk.prefs);
      if (prev) qc.setQueryData<Prefs>(nk.prefs, { ...prev, [category]: pref });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(nk.prefs, ctx.prev);
    },
    onSuccess: (next) => qc.setQueryData(nk.prefs, next),
  });
}
